import * as Sentry from '@sentry/react';
import useSafeBarHeight from '@web/common/src/hooks/useStatusBarHeight';
import { RouteItem } from '@web/common/src/routes';
import { LanguageType } from '@web/common/types/language';
import { iOS, isWebview } from '@web/common/src/utils/env';
import { kkBridgeCallAsync, kkBridgeHasMethod } from '@web/common/src/utils/jsBridge';
import { nativeApi } from '@web/common/src/utils/nativeApi';
import { NotFound, PageErrorBoundary } from '@web/components';
// byClaude-start 2026-09-23 16:19:10 byClaude: 保存当前应用的语言初始化状态。
import { Suspense, useEffect, useLayoutEffect, useRef, useState } from 'react';
// byClaude-end 2026-09-23 16:19:10
import { useNavigate } from 'react-router';
import { Route, Routes, useLocation } from 'react-router-dom';
import 'virtual:windi.css';
import { getUserInfoApi } from './appApi';
import { AppName } from './AppGlobal';
import billMgmtRoutes from './billMgmtRoutes';
import GlobalDialog from './components/GlobalDialog';
import { Spinner } from './components/Spinner';
// byClaude-start 2026-08-24 16:15:00 全局广告位，基于 sessionStorage 控制整个会话中的显示状态
import AdBanner from './pages/pub/index/AdBanner';
// byClaude-end 2026-08-24 16:15:00

import routes from './routes';
import './style.less';
import './styles/index.less';
import { getNacosConfig } from './utils/configUtils';
import { changeLanguage } from './utils/languageutils';
import { createNativeNavigateHandler } from './utils/nativeNavigate';

function App() {
  const location = useLocation();
  const isApp = isWebview();

  const [statusBarHeight] = useSafeBarHeight();

  const navigate = useNavigate();

  const [currentRoutes, setCurrentRoutes] = useState<RouteItem[]>([]);

  // byClaude-start 2026-09-23 16:19:10 byClaude: 无语言参数的页面跳转沿用当前语言。
  const languageInitialized = useRef(false);
  const rawLanguage = new URLSearchParams(location.search).get('lang')?.toLowerCase();
  const urlLanguage =
    rawLanguage === 'sc'
      ? 'cn'
      : rawLanguage === 'tc'
      ? 'zh'
      : ['en', 'zh', 'cn'].includes(rawLanguage || '')
      ? (rawLanguage as LanguageType)
      : undefined;

  useEffect(() => {
    if (!urlLanguage && languageInitialized.current) return;
    let cancelled = false;
    (async () => {
      const language = urlLanguage || (await nativeApi.getLanguage());
      if (cancelled) return;
      changeLanguage(language);
      document.body.classList.toggle('en', language === 'en');
      languageInitialized.current = true;
    })();
    return () => {
      cancelled = true;
    };
  }, [urlLanguage]);

  useEffect(() => {
    (async () => {
      Sentry.setTag('appVersion', (await nativeApi.getNativeAppVersion?.()) || 'pureH5');
    })();
  }, []);

  useEffect(() => {
    window.navigateFunc = createNativeNavigateHandler(navigate);
  }, [navigate]);
  // byClaude-end 2026-09-23 16:19:10

  useEffect(() => {
    if (kkBridgeHasMethod('kk_naviBarHeight')) {
      let a = kkBridgeCallAsync('kk_naviBarHeight');
      const devicePixelRatio = window.devicePixelRatio || 1;

      a /= devicePixelRatio;
      const fotterHeight = `${a}px`;
      document?.documentElement?.style?.setProperty('--safe-area-inset-bottom', fotterHeight);
    } else {
      document?.documentElement?.style?.setProperty('--safe-area-inset-bottom', '0px');
    }

    document?.documentElement?.style?.setProperty('--safe-bar-height', `${statusBarHeight}px`);

    if (iOS()) {
      // iOS 中，将值设置为 env(--iOS-safe-area-inset-bottom)
      document?.documentElement?.style?.setProperty(
        '--iOS-safe-area-inset-bottom',
        'env(safe-area-inset-bottom)'
      );
      document?.documentElement?.style?.setProperty(
        '--iOS-safe-area-inset-top',
        'env(safe-area-inset-top)'
      );
    }
  }, [statusBarHeight]);

  useEffect(() => {
    (async function fn() {
      const route = routes.find((item) => item.path === location.pathname);
      if (!route) return;
      if (!isApp) {
        document.title = route.meta.name || import.meta.env.VITE_GLOB_APP_TITLE || '维信卡卡贷';
      }
    })();
  }, [location]);

  useEffect(() => {
    // 注册公共属性
    sensors.registerPage({
      platform_type: 'H5',
      $ip: '',
    });
    sensors.use('PageLeave', {
      isCollectUrl(url = window.location.href) {
        console.log('PageLeave', url);
        // url 为要采集页面浏览时长的页面地址。
        return true; // 采集
        // return false; // 不采集
      },
    });
    Promise.resolve().then(() => {
      // 尽快初始化Nacos配置
      getNacosConfig([
        'app.newversion.cashout.select.firstcoupon',
        'app.pay.dev.order.time.switch',
        'app.pay.dev.time.switch',
        'pay.paymentMinLimit',
        'app.cl.pay.cl.support.clear.card.switch',
        'app.preconfirmloan.cl.initialperiod',
        'app.preconfirmloan.cmc.initialperiod',
        'app.preconfirmloan.kkc.initialperiod',
        'app.newversion.h5process.allnewversion',
        'google.app.download.switch',
        'pay.payCombinationLoanCheckSwitch', // 是否开启组合贷贷款
        'product.cmc.open', // 是否开启cmc全员开放
        'credit.clOrCmc.rate.dialog',
        'user.login.h5LoginNotCheckIdCard', // 是否开启h5登录不检查身份证
        'confirm.loan.show.youtube.link', // 是否显示youtube链接
        'cl.credit.approval.success.newuser.bubbleText',
        'cl.credit.approval.success.unlock.bubbleText',
        'cl.credit.approval.success.newuser.bubbleTextEn',
        'cl.credit.approval.success.unlock.bubbleTextEn',
        'cl.credit.approval.success.newuser.bubbleTextCn',
        'cl.credit.approval.success.unlock.bubbleTextCn',
        'app.pay.interestCategory', // 利息计算方式
        'pay.setTimeoutDurationHsbcEdda', // eDDA HSBC OTP 超时时长(分钟)
        'pay.setOtpTimeoutDurationHsbcEdda', // HSBC OTP 倒计时超时时间（秒）
      ]);
      setTimeout(() => {
        getUserInfoApi({ isIntercept: false });
      }, 0); // 没必要一启动就去获取用户信息
    });
  }, []);

  useLayoutEffect(() => {
    if (window.appName === AppName.BillManagement) {
      setCurrentRoutes(billMgmtRoutes);
    } else {
      setCurrentRoutes(routes);
    }
  }, []);

  return (
    <Suspense fallback={<Spinner />}>
      <Sentry.ErrorBoundary>
        <PageErrorBoundary>
          {/* byClaude-start 2026-08-24 16:15:00 全局广告位，在整个会话中持续显示直到用户手动关闭 */}
          <AdBanner />
          {/* byClaude-end 2026-08-24 16:15:00 */}
          <Routes>
            {currentRoutes.map((v) => (
              <Route key={v.path} path={v.path} element={v.component} />
            ))}
            {/* 添加pub前缀路由，用以展示无需登录的页面 */}
            <Route key="404" path="*" element={<NotFound />} />
          </Routes>
          <GlobalDialog />
        </PageErrorBoundary>
      </Sentry.ErrorBoundary>
    </Suspense>
  );
}

export default App;
// Contains claude-generated edits.
